/**
 * The tenant alert engine's codes about what a tenant's workload can do: place
 * its builds, keep its apps running, and grow the pool both stand on.
 *
 * Fingerprint for every code here: the tenant (ORGANIZATION scope), entity
 * `vibe_cluster`, entityId the cluster's organizationId — one row per tenant,
 * with the builds, jobs or allocations it is about named on the payload.
 */
import {
  servingUnconfirmedCausesSentence,
  servingUnconfirmedNextStepsSentence
} from "../../../vibe/serving-unconfirmed-copy";
import type { IssueDefinition } from "../../issue-definition";
import { ISSUE_CATEGORY } from "../../issue-enums";

export const VIBE_ALERT_WORKLOAD_ISSUES = {
  /**
   * Paging: a blocked evaluation is a build or an app slot the scheduler cannot
   * place, so a customer's deploy is waiting on it now and will time out.
   */
  VIBE_ALERT_NOMAD_BLOCKED_EVAL: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "A Nomad evaluation on this tenant has been BLOCKED for longer than the policy allows: the scheduler could not place a job and parked it waiting for capacity. The payload names the blocked jobs, the oldest evaluation's age, and the build each one is.",
    severity: "high",
    page: true,
    remedy:
      "Read the blocked jobs on the payload and compare what each asks for with the free capacity on the tenant's app nodes (`VIBE_ALERT_BUILD_HOLE` names the shortfall when it is a build). Scale the app pool out, or drain what holds the capacity. A blocked evaluation also leaves when its job is purged unplaced, so check the build's own outcome before reading this row's resolve as a fix."
  },

  /**
   * Paging: an allocation restarting faster than the bar is an app or a
   * platform task that is down between its restarts.
   */
  VIBE_ALERT_ALLOC_CRASH_LOOP: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "A live allocation on this tenant has restarted more than the policy allows in the last hour. The payload names each crash-looping task and the last exit code of the worst one: 137 is an OOM kill, 139 a segfault, 0 a clean exit it should not have made.",
    severity: "high",
    page: true,
    remedy:
      "Read the exit code on the payload. 137 means the task outgrew its memory — raise its reservation or find the leak. Any other code is the task's own failure — read its stderr in the tenant's Nomad. This resolves on its own once no allocation restarts past the bar."
  },

  /**
   * Not paging: a pool not managed the way the capacity census requires is
   * misconfigured, and its consequence — no room for the next build — pages
   * through `VIBE_ALERT_BUILD_HOLE`.
   */
  VIBE_ALERT_APP_POOL_CAPACITY: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "This serving tenant's app pool is not managed the way the capacity census requires, for longer than the degraded window: not exactly one autoscaler running, a live policy that disagrees with the stack outputs, or a client group that does not drain a node before removing it. The payload says which. While a reconcile pause holds the tenant, a drift in the client group's drain hook or `AZRebalance` suspension — which only a converge repairs — is listed on the pause's row instead; every other violation still raises this.",
    severity: "medium",
    remedy:
      "Read the verdict on the payload. An autoscaler count other than one is the tenant's Nomad job; a policy mismatch is fixed by converging the tenant's stack; a missing self-drain hook or an unsuspended `AZRebalance` is the client group's configuration. This resolves on its own once the census accepts the pool."
  },

  /**
   * Paging: no ready node with room for the next build means the next build
   * cannot be placed — at once when one is already waiting on a pool nothing can
   * grow.
   */
  VIBE_ALERT_BUILD_HOLE: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "No ready, eligible app node on this tenant has the room the next build needs (the app pool's hole) for longer than the policy allows — or a build is already waiting on a pool nothing can grow. The payload names the shortfall per node and any waiting build.",
    severity: "high",
    page: true,
    remedy:
      "Read the shortfall on the payload. Where an autoscaler manages the pool, check why it has not added a node (its cooldown, its maximum, a launch failure in the Auto Scaling group's activity). Where none does, add a node to the tenant's client group by hand. This resolves on its own once a node has the hole."
  },

  /**
   * Paging: a deployment the control plane holds HEALTHY that its agent does not
   * confirm still serves its last placed job, and nothing new can be placed for
   * it — a customer's next deploy of it will not land.
   */
  VIBE_ALERT_SERVING_DEPLOYMENT_UNCONFIRMED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message: `An app marked live on this tenant has not been confirmed working by its cluster for longer than the policy allows. The payload names each app and which of these is true of it: ${servingUnconfirmedCausesSentence()}`,
    severity: "high",
    page: true,
    remedy: `Each app on the payload says what to do next. ${servingUnconfirmedNextStepsSentence()} This resolves on its own once the cluster confirms every live app.`
  },

  /**
   * Paging: nothing looks down — the app keeps serving from the fenced node — but
   * the node cannot leave while it holds the slot, so the roll or scale-in that
   * fenced it fails after its one-hour wait.
   */
  VIBE_ALERT_RELOCATION_STRANDED: {
    category: ISSUE_CATEGORY.OBSERVABILITY,
    message:
      "An app slot on this tenant is still on a node a client roll or a scale-in fenced, for longer than the policy allows: its make-before-break move off the node never finished. The payload names each stranded deployment, its slot job, the node and the allocation.",
    severity: "high",
    page: true,
    remedy:
      "On the tenant's Nomad, run `nomad job status` on the deployment's OTHER color — a copy that is running but whose deployment reads failed, or that is still pending placement, is the move that stalled. Never drain the fenced node: a count=1 app stops before its copy is healthy. This resolves on its own once no slot of the deployment is on a fenced node."
  }
} as const satisfies Record<string, IssueDefinition>;
